import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import NewsItemTable from '../../../components/tables/NewsItemTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useNewsItemApi } from '../../../hooks/useNewsItemApi';
import { useContentCategoryApi } from '../../../hooks/useContentCategoryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { NewsItemDTO, ContentCategoryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const NewsItemList: React.FC = () => {
  const { search, remove, extract, isLoading, error } = useNewsItemApi();
  const { search: searchContentCategories } = useContentCategoryApi();
  const [items, setItems] = useState<NewsItemDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [contentCategories, setContentCategories] = useState<ContentCategoryDTO[]>([]);

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Deactivated' },
  ];

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'News Items', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching News Items:', error);
    }
  };

  const fetchContentCategories = async () => {
    try {
      const response = await searchContentCategories({
        filters: { isActive: true },
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setContentCategories(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Content Categories:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchContentCategories();
  }, []);

  // isActive comes off the dropdown as a string — the API expects a bool or nothing.
  const buildFilters = () => ({
    ...searchFilters,
    isActive: searchFilters.isActive === '' || searchFilters.isActive === undefined
      ? undefined
      : searchFilters.isActive === 'true',
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, buildFilters());
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: NewsItemDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting NewsItem:', error);
    }
  };

  // Add export handler
  const handleExport = async () => {
    try {
      const response = await extract({
        filters: buildFilters(),
        page: currentPage,
        pageSize: pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting News Items:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/newsitems"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Content.NewsItem.View'
    >
      <Card
        title="Manage News Items"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button
              type='button'
              variant='secondary'
              onClick={handleExport}
            >
              Export
            </Button>
            <AddNewButton
              onClick={() => navigate('/admin/newsitems/create')}
            />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
              placeholder="Search by Title"
            />
            <FormField
              id="contentCategoryId"
              label="Search by Content Category"
              type="dropdown"
              value={searchFilters.contentCategoryId || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, contentCategoryId: e.target.value })}
              placeholder="All Content Categories"
              options={contentCategories.map((category) => ({
                value: category.id,
                label: category.title ?? '',
              }))}
            />
            <FormField
              id="isActive"
              label="Search by Status"
              type="dropdown"
              value={searchFilters.isActive || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, isActive: e.target.value })}
              placeholder="All Statuses"
              options={statusOptions}
            />

          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <NewsItemTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/newsitems/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default NewsItemList;